import React, { useState, useEffect, useRef } from 'react';
import { 
  Package, 
  Plus, 
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  AlertTriangle,
  TrendingUp,
  Box,
  DollarSign,
  Upload,
  FileSpreadsheet,
  Sparkles,
  Loader2,
  Image as ImageIcon,
  X,
  Check,
  Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const units = ['pcs', 'kg', 'g', 'liters', 'ml', 'boxes', 'packs', 'meters', 'units'];
const defaultCategories = ['Raw Materials', 'Finished Goods', 'Packaging', 'Consumables', 'Equipment', 'Office Supplies', 'Other'];

// Industry-specific categories
const INDUSTRY_CATEGORIES = {
  smoke_shop: ['Cigarettes', 'Vapes', 'E-Liquids', 'Rolling Papers', 'Lighters', 'CBD Products', 'Accessories', 'Tobacco'],
  mobile_electronics: ['Phones', 'Cases', 'Chargers', 'Screen Protectors', 'Cables', 'Earbuds', 'Repairs', 'Parts'],
  retail_store: ['General', 'Popular', 'New Arrivals', 'Sale', 'Essentials', 'Gifts'],
  restaurant_cafe: ['Drinks', 'Food', 'Desserts', 'Combos', 'Specials', 'Sides'],
  grocery_convenience: ['Snacks', 'Beverages', 'Tobacco', 'Lottery', 'Dairy', 'Bread'],
  pharmacy: ['OTC Meds', 'Vitamins', 'First Aid', 'Personal Care', 'Baby Care', 'Rx'],
  clothing_fashion: ["Men's", "Women's", 'Kids', 'Shoes', 'Accessories', 'Sale'],
  salon_spa: ['Haircut', 'Color', 'Nails', 'Facial', 'Massage', 'Products'],
  auto_hardware: ['Motor Oil', 'Batteries', 'Tools', 'Parts', 'Tires', 'Cleaning'],
  wholesale_distribution: ['Bulk Items', 'Case Deals', 'New Stock', 'Clearance', 'Seasonal', 'Popular'],
};

// Product Icon Options
const PRODUCT_ICONS = {
  smoke_shop: ['🚬', '💨', '🔥', '🌿', '📜', '🎁', '💊', '🧪'],
  mobile_electronics: ['📱', '💻', '🔌', '🎧', '📺', '🔋', '💾', '🖥️'],
  retail_store: ['👕', '👗', '👟', '🎒', '⌚', '💍', '🕶️', '👜'],
  restaurant_cafe: ['☕', '🍔', '🍕', '🥤', '🍰', '🥗', '🍟', '🌮'],
  grocery_convenience: ['🥤', '🍪', '🍫', '🥛', '🍞', '🧃', '🍎', '🥚'],
  pharmacy: ['💊', '💉', '🩹', '🧴', '🩺', '💊', '🌡️', '🧪'],
  clothing_fashion: ['👕', '👗', '👖', '👟', '👜', '🧥', '👒', '🧣'],
  salon_spa: ['✂️', '💅', '💆', '🧖', '💇', '🪮', '💄', '🧴'],
  auto_hardware: ['🔧', '🔩', '🛠️', '⚙️', '🔋', '🛞', '🚗', '🔌'],
  wholesale_distribution: ['📦', '🏷️', '📋', '🚚', '💰', '📊', '🏪', '💼'],
  default: ['📦', '🏷️', '⭐', '🎁', '💼', '🛒', '📋', '✨'],
};

const Products = () => {
  const { currencySymbol } = useSettings();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({});
  const [adjustData, setAdjustData] = useState({ quantity: 0, reason: '' });
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);
  
  // CSV Import
  const [showCSVDialog, setShowCSVDialog] = useState(false);
  const [csvData, setCSVData] = useState([]);
  const [csvFile, setCSVFile] = useState(null);
  const [importingCSV, setImportingCSV] = useState(false);
  const fileInputRef = useRef(null);
  
  // AI Image Generation
  const [generatingImage, setGeneratingImage] = useState(false);
  const [useIcon, setUseIcon] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState('📦');
  
  // Business Type for categories
  const [businessType, setBusinessType] = useState('default');
  const [categories, setCategories] = useState(defaultCategories);

  useEffect(() => {
    fetchProducts();
    loadBusinessType();
  }, []);
  
  // Load user's business type for categories
  const loadBusinessType = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/subscriptions/my-access`);
      if (res.data?.business_type) {
        const bt = res.data.business_type;
        setBusinessType(bt);
        setCategories(INDUSTRY_CATEGORIES[bt] || defaultCategories);
      }
    } catch (err) {
      console.error('Failed to load business type:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/products`);
      setProducts(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const dataToSave = {
        ...formData,
        sku: formData.sku || `SKU-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      };
      
      if (editingProduct) {
        await axios.put(`${API_URL}/api/procurement/products/${editingProduct.id}`, dataToSave);
        toast.success('Product updated');
      } else {
        await axios.post(`${API_URL}/api/procurement/products`, dataToSave);
        toast.success('Product added');
      }
      setShowDialog(false);
      setEditingProduct(null);
      setFormData({});
      setUseIcon(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleAdjustStock = async () => {
    try {
      await axios.post(
        `${API_URL}/api/procurement/products/${editingProduct.id}/adjust-stock?quantity=${adjustData.quantity}&reason=${encodeURIComponent(adjustData.reason || '')}`
      );
      toast.success('Stock adjusted');
      setShowAdjustDialog(false);
      setEditingProduct(null);
      setAdjustData({ quantity: 0, reason: '' });
      fetchProducts();
    } catch (err) {
      toast.error('Failed to adjust stock');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this product?')) {
      try {
        await axios.delete(`${API_URL}/api/procurement/products/${id}`);
        toast.success('Product deleted');
        fetchProducts();
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setFormData({ ...product });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingProduct(null);
    setFormData({ unit: 'pcs', category: categories[0] || 'Raw Materials' });
    setUseIcon(false);
    setSelectedIcon('📦');
    setShowDialog(true);
  };

  const openAdjust = (product) => {
    setEditingProduct(product);
    setAdjustData({ quantity: 0, reason: '' });
    setShowAdjustDialog(true);
  };

  // AI Image Generation
  const generateProductImage = async () => {
    if (!formData.name) {
      toast.error('Please enter product name first');
      return;
    }
    
    setGeneratingImage(true);
    try {
      const industryName = businessType.replace(/_/g, ' ');
      const prompt = `Product photo of ${formData.name} for ${industryName} store, professional product photography, white background, high quality, commercial style`;
      
      const res = await axios.post(`${API_URL}/api/ai/generate-image`, {
        prompt: prompt,
        size: '256x256'
      });
      
      if (res.data?.image_url) {
        setFormData(prev => ({ ...prev, image_url: res.data.image_url }));
        setUseIcon(false);
        toast.success('Product image generated!');
      }
    } catch (err) {
      console.error('Failed to generate image:', err);
      toast.error('Image generation failed. Try using an icon instead.');
    } finally {
      setGeneratingImage(false);
    }
  };

  // CSV File Handling
  const handleCSVFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setCSVFile(file);
    parseCSVFile(file);
  };

  const parseCSVFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file is empty or has no data rows');
        return;
      }
      
      // Parse header
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
      
      // Map headers to our fields
      const headerMap = {
        'name': 'name',
        'product name': 'name',
        'product': 'name',
        'sku': 'sku',
        'barcode': 'barcode',
        'upc': 'barcode',
        'category': 'category',
        'cost': 'cost_price',
        'cost price': 'cost_price',
        'cost_price': 'cost_price',
        'price': 'selling_price',
        'sell price': 'selling_price',
        'selling price': 'selling_price',
        'selling_price': 'selling_price',
        'sell_price': 'selling_price',
        'stock': 'stock_quantity',
        'quantity': 'stock_quantity',
        'stock_quantity': 'stock_quantity',
        'stock quantity': 'stock_quantity',
        'unit': 'unit',
        'description': 'description',
        'reorder': 'reorder_level',
        'reorder level': 'reorder_level',
        'reorder_level': 'reorder_level',
      };
      
      const mappedHeaders = headers.map(h => headerMap[h] || h);
      
      // Parse data rows
      const parsedData = [];
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === 0) continue;
        
        const row = { selected: true, id: `csv-${i}` };
        mappedHeaders.forEach((header, idx) => {
          if (values[idx] !== undefined) {
            row[header] = values[idx].trim().replace(/^["']|["']$/g, '');
          }
        });
        
        // Validate required fields
        if (row.name) {
          row.cost_price = parseFloat(row.cost_price) || 0;
          row.selling_price = parseFloat(row.selling_price) || row.cost_price * 1.3;
          row.stock_quantity = parseInt(row.stock_quantity) || 0;
          row.reorder_level = parseInt(row.reorder_level) || 10;
          row.unit = row.unit || 'pcs';
          row.category = row.category || categories[0] || 'General';
          parsedData.push(row);
        }
      }
      
      if (parsedData.length === 0) {
        toast.error('No valid products found in CSV. Make sure you have a "name" column.');
        return;
      }
      
      setCSVData(parsedData);
      setShowCSVDialog(true);
      toast.success(`Found ${parsedData.length} products in CSV`);
    };
    reader.readAsText(file);
  };

  // Parse CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"' && (i === 0 || line[i-1] !== '\\')) {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const handleCSVImport = async () => {
    const selectedProducts = csvData.filter(p => p.selected);
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    
    setImportingCSV(true);
    let added = 0;
    let failed = 0;
    
    for (const product of selectedProducts) {
      try {
        await axios.post(`${API_URL}/api/procurement/products`, {
          name: product.name,
          sku: product.sku || `SKU-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
          barcode: product.barcode || '',
          category: product.category,
          cost_price: product.cost_price,
          selling_price: product.selling_price,
          stock_quantity: product.stock_quantity,
          reorder_level: product.reorder_level,
          unit: product.unit,
          description: product.description || '',
        });
        added++;
      } catch (err) {
        console.error('Failed to import product:', product.name, err);
        failed++;
      }
    }
    
    setImportingCSV(false);
    setShowCSVDialog(false);
    setCSVData([]);
    setCSVFile(null);
    
    if (added > 0) {
      toast.success(`Imported ${added} products${failed > 0 ? `, ${failed} failed` : ''}`);
      fetchProducts();
    } else {
      toast.error('Failed to import products');
    }
  };

  const toggleCSVProductSelection = (id) => {
    setCSVData(prev => prev.map(p => 
      p.id === id ? { ...p, selected: !p.selected } : p
    ));
  };

  const selectAllCSV = (selected) => {
    setCSVData(prev => prev.map(p => ({ ...p, selected })));
  };

  const downloadCSVTemplate = () => {
    const template = `name,sku,category,cost_price,selling_price,stock_quantity,unit,description,reorder_level
"Sample Product 1","SKU-001","${categories[0] || 'General'}",10.00,15.99,100,pcs,"Sample description",10
"Sample Product 2","SKU-002","${categories[1] || 'General'}",5.50,8.99,50,pcs,"Another product",5`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const filteredProducts = products
    .filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase()))
    .filter(p => filterCategory === 'all' || p.category === filterCategory)
    .filter(p => !showLowStock || (p.stock_quantity <= p.reorder_level));

  const stats = {
    total: products.length,
    lowStock: products.filter(p => p.stock_quantity <= p.reorder_level).length,
    totalValue: products.reduce((sum, p) => sum + (p.stock_quantity * p.cost_price), 0),
    totalItems: products.reduce((sum, p) => sum + p.stock_quantity, 0)
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Products & Inventory</h1>
          <p className="text-muted-foreground">Manage product catalog and stock levels.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadCSVTemplate} className="gap-2" data-testid="download-template-btn">
            <Download className="h-4 w-4" /> Template
          </Button>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2" data-testid="csv-import-btn">
            <FileSpreadsheet className="h-4 w-4" /> Import CSV
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleCSVFileSelect}
            className="hidden"
          />
          <Button onClick={openNew} className="gap-2" data-testid="add-product-btn">
            <Plus className="h-4 w-4" /> Add Product
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Package className="h-8 w-8 text-blue-500" /><div><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">Products</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Box className="h-8 w-8 text-purple-500" /><div><div className="text-2xl font-bold">{stats.totalItems.toLocaleString()}</div><div className="text-sm text-muted-foreground">Total Stock</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><DollarSign className="h-8 w-8 text-emerald-500" /><div><div className="text-2xl font-bold">{currencySymbol} {stats.totalValue.toLocaleString()}</div><div className="text-sm text-muted-foreground">Stock Value</div></div></div></CardContent></Card>
        <Card className={stats.lowStock > 0 ? 'border-amber-300 bg-amber-50/50' : ''}><CardContent className="pt-6"><div className="flex items-center gap-3"><AlertTriangle className={`h-8 w-8 ${stats.lowStock > 0 ? 'text-amber-500' : 'text-slate-400'}`} /><div><div className="text-2xl font-bold">{stats.lowStock}</div><div className="text-sm text-muted-foreground">Low Stock</div></div></div></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-10" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={showLowStock ? 'default' : 'outline'} onClick={() => setShowLowStock(!showLowStock)} className="gap-2">
          <AlertTriangle className="h-4 w-4" /> Low Stock Only
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Cost Price</TableHead>
              <TableHead className="text-right">Stock</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.map((product) => {
              const isLow = product.stock_quantity <= product.reorder_level;
              return (
                <TableRow key={product.id} className={isLow ? 'bg-amber-50/50' : ''}>
                  <TableCell>
                    <div className="font-medium">{product.name}</div>
                    {product.description && <div className="text-xs text-muted-foreground truncate max-w-xs">{product.description}</div>}
                  </TableCell>
                  <TableCell className="font-mono text-muted-foreground">{product.sku}</TableCell>
                  <TableCell><Badge variant="outline">{product.category}</Badge></TableCell>
                  <TableCell className="text-right font-mono">{currencySymbol} {product.cost_price?.toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    <span className={`font-bold ${isLow ? 'text-amber-600' : ''}`}>{product.stock_quantity}</span>
                    <span className="text-muted-foreground text-sm ml-1">{product.unit}</span>
                  </TableCell>
                  <TableCell className="text-right font-mono">{currencySymbol} {(product.stock_quantity * product.cost_price).toLocaleString()}</TableCell>
                  <TableCell>
                    {isLow ? (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300 gap-1">
                        <AlertTriangle className="h-3 w-3" /> Low Stock
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-emerald-600">In Stock</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(product)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openAdjust(product)}><TrendingUp className="h-4 w-4 mr-2" /> Adjust Stock</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(product.id)}>
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredProducts.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-32 text-center">
                  <Package className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">No products found</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Product Dialog - Enhanced with AI Image & Icons */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[65vh] overflow-y-auto">
            {/* Product Image Section */}
            <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
              <Label className="text-sm font-medium mb-3 block">Product Image</Label>
              <div className="flex gap-4">
                {/* Image Preview */}
                <div className="h-24 w-24 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center bg-white dark:bg-slate-800 overflow-hidden flex-shrink-0">
                  {useIcon ? (
                    <span className="text-4xl">{selectedIcon}</span>
                  ) : formData.image_url ? (
                    <img src={formData.image_url} alt="Product" className="h-full w-full object-cover" />
                  ) : (
                    <Package className="h-10 w-10 text-slate-300" />
                  )}
                </div>
                
                {/* Image Options */}
                <div className="flex-1 space-y-3">
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generateProductImage}
                      disabled={generatingImage || !formData.name}
                      className="gap-2"
                      data-testid="generate-ai-image-btn"
                    >
                      {generatingImage ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
                      ) : (
                        <><Sparkles className="h-4 w-4" /> Generate with AI</>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant={useIcon ? "default" : "outline"}
                      size="sm"
                      onClick={() => setUseIcon(!useIcon)}
                      className="gap-2"
                    >
                      <Package className="h-4 w-4" /> Use Icon
                    </Button>
                  </div>
                  
                  {/* Icon Selection */}
                  {useIcon && (
                    <div className="flex flex-wrap gap-1">
                      {(PRODUCT_ICONS[businessType] || PRODUCT_ICONS['default']).map(icon => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => {
                            setSelectedIcon(icon);
                            setFormData(prev => ({ ...prev, image_url: `icon:${icon}` }));
                          }}
                          className={`w-9 h-9 rounded-lg border-2 flex items-center justify-center text-lg transition-all ${
                            selectedIcon === icon 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' 
                              : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
                          }`}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Manual URL Input */}
                  {!useIcon && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Or paste URL:</span>
                      <Input 
                        className="h-8 text-xs flex-1" 
                        placeholder="https://..." 
                        value={formData.image_url?.startsWith('data:') ? '' : (formData.image_url || '')}
                        onChange={e => setFormData({...formData, image_url: e.target.value})}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SKU *</Label>
                <Input value={formData.sku || ''} onChange={e => setFormData({...formData, sku: e.target.value})} placeholder="SKU-001 (auto-generated if empty)" />
              </div>
              <div className="space-y-2">
                <Label>Barcode / UPC</Label>
                <Input value={formData.barcode || ''} onChange={e => setFormData({...formData, barcode: e.target.value})} placeholder="Scan or enter barcode" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Product Name *</Label>
                <Input value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Product name" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category || categories[0]} onValueChange={v => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    {/* Include default categories as fallback */}
                    {!categories.some(c => defaultCategories.includes(c)) && defaultCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} placeholder="Optional description" />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={formData.unit || 'pcs'} onValueChange={v => setFormData({...formData, unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{units.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cost Price</Label>
                <Input type="number" step="0.01" value={formData.cost_price || ''} onChange={e => setFormData({...formData, cost_price: parseFloat(e.target.value) || 0})} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Selling Price</Label>
                <Input type="number" step="0.01" value={formData.selling_price || ''} onChange={e => setFormData({...formData, selling_price: parseFloat(e.target.value) || 0})} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Stock Qty</Label>
                <Input type="number" value={formData.stock_quantity || 0} onChange={e => setFormData({...formData, stock_quantity: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reorder Level</Label>
                <Input type="number" value={formData.reorder_level || 10} onChange={e => setFormData({...formData, reorder_level: parseFloat(e.target.value) || 0})} />
                <p className="text-xs text-muted-foreground">Alert when stock falls below this level</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!formData.name}>{editingProduct ? 'Update' : 'Add Product'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Adjust Stock - {editingProduct?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <div className="text-sm text-muted-foreground">Current Stock</div>
              <div className="text-3xl font-bold">{editingProduct?.stock_quantity} {editingProduct?.unit}</div>
            </div>
            <div className="space-y-2">
              <Label>Adjustment Quantity</Label>
              <Input type="number" value={adjustData.quantity} onChange={e => setAdjustData({...adjustData, quantity: parseFloat(e.target.value) || 0})} placeholder="Use negative for decrease" />
              <p className="text-xs text-muted-foreground">Use positive number to increase, negative to decrease</p>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={adjustData.reason} onChange={e => setAdjustData({...adjustData, reason: e.target.value})} rows={2} placeholder="Stock count adjustment..." />
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
              <div className="text-sm text-blue-600">New Stock</div>
              <div className="text-3xl font-bold text-blue-700">{(editingProduct?.stock_quantity || 0) + adjustData.quantity} {editingProduct?.unit}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>Cancel</Button>
            <Button onClick={handleAdjustStock}>Apply Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={showCSVDialog} onOpenChange={setShowCSVDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Import Products from CSV
            </DialogTitle>
            <DialogDescription>
              Review and select products to import. {csvData.filter(p => p.selected).length} of {csvData.length} selected.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Select All / None */}
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => selectAllCSV(true)}>
                  <Check className="h-4 w-4 mr-1" /> Select All
                </Button>
                <Button variant="outline" size="sm" onClick={() => selectAllCSV(false)}>
                  <X className="h-4 w-4 mr-1" /> Deselect All
                </Button>
              </div>
              <Badge variant="secondary">
                {csvData.filter(p => p.selected).length} products selected
              </Badge>
            </div>
            
            {/* Products Table */}
            <div className="border rounded-lg max-h-[45vh] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white dark:bg-slate-950 z-10">
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Sell Price</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvData.map((product) => (
                    <TableRow 
                      key={product.id} 
                      className={`cursor-pointer ${product.selected ? 'bg-blue-50/50 dark:bg-blue-950/30' : 'opacity-60'}`}
                      onClick={() => toggleCSVProductSelection(product.id)}
                    >
                      <TableCell>
                        <div className={`h-5 w-5 rounded border-2 flex items-center justify-center ${
                          product.selected ? 'bg-blue-500 border-blue-500' : 'border-slate-300'
                        }`}>
                          {product.selected && <Check className="h-3 w-3 text-white" />}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{product.sku || '-'}</TableCell>
                      <TableCell><Badge variant="outline">{product.category}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{currencySymbol}{product.cost_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono">{currencySymbol}{product.selling_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{product.stock_quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCSVDialog(false);
              setCSVData([]);
              setCSVFile(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCSVImport} 
              disabled={importingCSV || csvData.filter(p => p.selected).length === 0}
              className="gap-2"
            >
              {importingCSV ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Importing...</>
              ) : (
                <><Upload className="h-4 w-4" /> Import {csvData.filter(p => p.selected).length} Products</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
